import React from 'react'
import { ExperienceInputField } from '../form'
import ExperienceCheckbox from '../form/ExperienceCheckbox'
import ExperienceCKEditor from './ExperienceCKEditor'
import PropTypes from 'prop-types'

const EnrollmentSubSections = ({
  stepKey,
  values,
  isStepEnabled,
  stepData,
  setStepData,
  setFieldValue,
  setIsAutoSaving,
  updateQuestDetails,
  Error,
  checkAndDisableMainStepIfNeeded,
}) => {
  const handleSectionToggle = async (
    sectionKey,
    fieldPath,
    newEnabledValue,
  ) => {
    setFieldValue(fieldPath, newEnabledValue)

    const updatedStepData = { ...stepData }
    updatedStepData[stepKey] = {
      ...updatedStepData[stepKey],
      [sectionKey]: {
        ...updatedStepData[stepKey][sectionKey],
        enabled: newEnabledValue,
      },
    }
    setStepData(updatedStepData)

    try {
      await updateQuestDetails(updatedStepData)
      if (checkAndDisableMainStepIfNeeded) {
        await checkAndDisableMainStepIfNeeded(updatedStepData)
      }
    } catch (error) {
      setFieldValue(fieldPath, !newEnabledValue)
      const revertedStepData = { ...stepData }
      revertedStepData[stepKey] = {
        ...revertedStepData[stepKey],
        [sectionKey]: {
          ...revertedStepData[stepKey][sectionKey],
          enabled: !newEnabledValue,
        },
      }
      setStepData(revertedStepData)
      Error.showError(error)
    } finally {
      setIsAutoSaving(false)
    }
  }

  const setEditorHeight = (editor, height = '140px') => {
    if (!editor) return
    editor.editing.view.change((writer) => {
      writer.setStyle(
        'min-height',
        height,
        editor.editing.view.document.getRoot(),
      )
    })
  }

  const flightEnabled =
    values[stepKey].flightInstructions?.enabled !== undefined
      ? values[stepKey].flightInstructions.enabled
      : true
  const insuranceEnabled =
    values[stepKey].travelInsurance?.enabled !== undefined
      ? values[stepKey].travelInsurance.enabled
      : true
  const passportEnabled =
    values[stepKey].passport?.enabled !== undefined
      ? values[stepKey].passport.enabled
      : true
  const headshotEnabled =
    values[stepKey].headshot?.enabled !== undefined
      ? values[stepKey].headshot.enabled
      : true

  return (
    <div className='mb-4 mt-3'>
      <div className='experience-field-head'>
        <h4>Travel document sections</h4>
      </div>
      <p className='experience-f-hint top text-dark'>
        Each section can be enabled on its own. If all four are off, this step
        is skipped.
      </p>

      <div className='experience-enrol-block'>
        <div className='experience-field-head'>
          <h4>Flight instructions (air ticket)</h4>
        </div>
        <ExperienceCheckbox
          id={`${stepKey}-flightInstructions-enabled`}
          name={`${stepKey}.flightInstructions.enabled`}
          label='Enable this section'
          checked={flightEnabled}
          disabled={!isStepEnabled}
          onChange={(e) =>
            handleSectionToggle(
              'flightInstructions',
              `${stepKey}.flightInstructions.enabled`,
              e.target.checked,
            )
          }
        />
        <div className='mt-12px'>
          <div className='experience-f mb-3'>
            <label className='experience-f-label'>General instructions</label>
            <ExperienceCKEditor
              name='flightInstructions.generalInstructions'
              data={
                values[stepKey].flightInstructions?.generalInstructions || ''
              }
              placeholder='Add general flight instructions'
              onReady={(editor) => setEditorHeight(editor)}
              onChange={(event, editor) => {
                setFieldValue(
                  `${stepKey}.flightInstructions.generalInstructions`,
                  editor.getData(),
                )
              }}
              disabled={!isStepEnabled || !flightEnabled}
            />
          </div>
          <div className='experience-f mb-3'>
            <label className='experience-f-label'>Arrival instructions</label>
            <ExperienceCKEditor
              name='flightInstructions.arrivalInstructions.content'
              data={
                values[stepKey].flightInstructions?.arrivalInstructions
                  ?.content || ''
              }
              placeholder='Add arrival instructions'
              onReady={(editor) => setEditorHeight(editor)}
              onChange={(event, editor) => {
                setFieldValue(
                  `${stepKey}.flightInstructions.arrivalInstructions.content`,
                  editor.getData(),
                )
              }}
              disabled={!isStepEnabled || !flightEnabled}
            />
          </div>
          <div className='experience-f mb-3'>
            <label className='experience-f-label'>Return instructions</label>
            <ExperienceCKEditor
              name='flightInstructions.returnInstructions.content'
              data={
                values[stepKey].flightInstructions?.returnInstructions
                  ?.content || ''
              }
              placeholder='Add return instructions'
              onReady={(editor) => setEditorHeight(editor)}
              onChange={(event, editor) => {
                setFieldValue(
                  `${stepKey}.flightInstructions.returnInstructions.content`,
                  editor.getData(),
                )
              }}
              disabled={!isStepEnabled || !flightEnabled}
            />
          </div>
        </div>
      </div>

      <div className='experience-enrol-block'>
        <div className='experience-field-head'>
          <h4>Travel insurance</h4>
        </div>
        <ExperienceCheckbox
          id={`${stepKey}-travelInsurance-enabled`}
          name={`${stepKey}.travelInsurance.enabled`}
          label='Enable this section'
          checked={insuranceEnabled}
          disabled={!isStepEnabled}
          onChange={(e) =>
            handleSectionToggle(
              'travelInsurance',
              `${stepKey}.travelInsurance.enabled`,
              e.target.checked,
            )
          }
        />
        <div className='experience-f mt-12px'>
          <ExperienceInputField
            name={`${stepKey}.travelInsurance.title`}
            label='Title'
            capitalizeFirstLetter={true}
            placeholder='Enter title'
            showErrorMessage={false}
            displayFlex={false}
            disabled={!isStepEnabled || !insuranceEnabled}
          />
        </div>
        <div className='experience-f mt-12px'>
          <label className='experience-f-label'>Description</label>
          <ExperienceCKEditor
            name='travelInsurance.description'
            data={values[stepKey].travelInsurance?.description || ''}
            placeholder='Enter description'
            onReady={(editor) => setEditorHeight(editor)}
            onChange={(event, editor) => {
              setFieldValue(
                `${stepKey}.travelInsurance.description`,
                editor.getData(),
              )
            }}
            disabled={!isStepEnabled || !insuranceEnabled}
          />
        </div>
      </div>

      <div className='experience-enrol-block'>
        <div className='experience-field-head'>
          <h4>Passport</h4>
        </div>
        <ExperienceCheckbox
          id={`${stepKey}-passport-enabled`}
          name={`${stepKey}.passport.enabled`}
          label='Enable this section'
          checked={passportEnabled}
          disabled={!isStepEnabled}
          onChange={(e) =>
            handleSectionToggle(
              'passport',
              `${stepKey}.passport.enabled`,
              e.target.checked,
            )
          }
        />
        <div className='experience-f mt-12px'>
          <label className='experience-f-label'>Description</label>
          <ExperienceCKEditor
            name='passport.description'
            data={values[stepKey].passport?.description || ''}
            placeholder='Enter description'
            onReady={(editor) => setEditorHeight(editor)}
            onChange={(event, editor) => {
              setFieldValue(`${stepKey}.passport.description`, editor.getData())
            }}
            disabled={!isStepEnabled || !passportEnabled}
          />
        </div>
      </div>

      <div className='experience-enrol-block'>
        <div className='experience-field-head'>
          <h4>Headshot</h4>
        </div>
        <ExperienceCheckbox
          id={`${stepKey}-headshot-enabled`}
          name={`${stepKey}.headshot.enabled`}
          label='Enable this section'
          checked={headshotEnabled}
          disabled={!isStepEnabled}
          onChange={(e) =>
            handleSectionToggle(
              'headshot',
              `${stepKey}.headshot.enabled`,
              e.target.checked,
            )
          }
        />
        <div className='experience-f mt-12px'>
          <label className='experience-f-label'>Description</label>
          <ExperienceCKEditor
            name='headshot.description'
            data={values[stepKey].headshot?.description || ''}
            placeholder='Enter description'
            onReady={(editor) => setEditorHeight(editor)}
            onChange={(event, editor) => {
              setFieldValue(`${stepKey}.headshot.description`, editor.getData())
            }}
            disabled={!isStepEnabled || !headshotEnabled}
          />
        </div>
      </div>
    </div>
  )
}

EnrollmentSubSections.propTypes = {
  stepKey: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  isStepEnabled: PropTypes.bool.isRequired,
  stepData: PropTypes.object.isRequired,
  setStepData: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setIsAutoSaving: PropTypes.func.isRequired,
  updateQuestDetails: PropTypes.func.isRequired,
  Error: PropTypes.object.isRequired,
  checkAndDisableMainStepIfNeeded: PropTypes.func,
}

export default EnrollmentSubSections
